import { useCallback, useMemo } from 'react';

import { isDefined, isNotDefined, MINUTE, parseJSON } from '@enterprise/core';
import { useSessionStorage } from '@enterprise/hooks';

import type { AuthorizeParameter } from '#context/permissions/types/authorize-parameter.type';
import type { PermissionsContentReturnType } from '#context/permissions/types/permission-context-return.type';
import type { PermissionsProviderProps } from '#context/permissions/types/permissions-provider.props';

/**
 * Hook that provides configuration and caching for permissions authorization.
 *
 * @param props - Configuration options for the permissions provider, excluding children.
 * @param props.isAuthorized - External function to check authorization for a given parameter.
 * @param props.sliding - Whether to use sliding expiration for cache entries. Defaults to `false`.
 * @param props.ttlInMilliseconds - Time-to-live for cache entries in milliseconds. Defaults to 1 minute.
 *
 * @returns An object containing:
 *   - `clearCache`: Function to clear the permissions cache.
 *   - `isAuthorized`: Async function to check if a parameter is authorized, using cache when possible.
 *
 * @remarks
 * - Uses session storage to cache authorization results for improved performance.
 * - Handles serialization/deserialization of cache entries.
 * - Supports sliding expiration for cache entries.
 */
export const usePermissionsProviderConfiguration = ({
  isAuthorized: externalIsAuthorized,
  sliding = false,
  ttlInMilliseconds = 1 * MINUTE,
}: Omit<PermissionsProviderProps, 'children'>): PermissionsContentReturnType => {
  const deserialize = useCallback((raw: string | undefined): Map<string, boolean> | undefined => {
    if (isNotDefined(raw)) {
      return undefined;
    }

    const result = parseJSON<[string, boolean][] | undefined>(raw);

    if (result.isFailure()) {
      return undefined;
    }

    return new Map(result.unwrap() ?? []);
  }, []);

  const serialize = useCallback((value: Map<string, boolean> | undefined): string => {
    return JSON.stringify([...(value?.entries() ?? [])]);
  }, []);

  const storageOptions = useMemo(() => {
    return {
      defaultTtlInMilliseconds: ttlInMilliseconds,
      namespace: 'permissions',
    };
  }, [ttlInMilliseconds]);

  const initialValue = useMemo(() => {
    return new Map<string, boolean>();
  }, []);

  const [cache, { clearNamespace, setValue: setCache }] = useSessionStorage('check', initialValue, {
    deserialize,
    serialize,
    storageOptions,
  });

  const clearCache = useCallback(() => {
    return new Promise<void>((resolve) => {
      clearNamespace();
      resolve();
    });
  }, [clearNamespace]);

  const isAuthorized = useCallback(
    async (check: AuthorizeParameter): Promise<boolean> => {
      try {
        const key = JSON.stringify(check);
        const current = cache ?? initialValue;
        const cachedValue = current.get(key);

        if (isDefined(cachedValue)) {
          return cachedValue;
        }

        const allowed = await externalIsAuthorized(check);

        current.set(key, allowed);
        setCache(current, { sliding, ttlInMilliseconds });

        return allowed;
      } catch {
        return false;
      }
    },
    [cache, externalIsAuthorized, initialValue, setCache, sliding, ttlInMilliseconds]
  );

  return {
    clearCache,
    isAuthorized,
  };
};
